import type { MedalScoreThresholds } from "@dailywars/core/db";
import {
  activeVictoryCondition,
  objectiveDescription,
  type BuildingTileKind,
  type GameState,
} from "@dailywars/core/engine";
import { useEffect, useState } from "react";

import { MedalThresholdScores } from "#/components/game/MedalThresholdScores";
import { Button } from "#/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "#/components/ui/dialog";
import { getBuildingIconSrc } from "#/game/buildingSprites";
import { useGameSelector, useGameStoreApi } from "#/game/GameStoreProvider";

function isFreshGameStart(state: GameState): boolean {
  return state.status === "in_progress" && state.currentDay === 1 && state.currentPlayer === "p1";
}

function objectiveBuildingKind(state: GameState): BuildingTileKind | null {
  const kind = activeVictoryCondition(state);
  if (kind === "capture") return "city";
  if (kind === "monuments") return "monument";
  return null;
}

export function ObjectiveDialog({
  medalThresholds,
}: {
  medalThresholds: MedalScoreThresholds | null;
}) {
  const api = useGameStoreApi();
  const localState = useGameSelector((s) => s.localState);
  const [open, setOpen] = useState(() => isFreshGameStart(api.getState().localState));
  const buildingKind = objectiveBuildingKind(localState);

  useEffect(() => {
    setOpen(isFreshGameStart(api.getState().localState));
  }, [api]);

  return (
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="sm:max-w-md">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-3xl font-semibold">Map Objective</DialogTitle>
          {buildingKind ? (
            <img
              src={getBuildingIconSrc(buildingKind, "neutral")}
              alt=""
              aria-hidden
              className="size-16 [image-rendering:pixelated]"
              draggable={false}
            />
          ) : null}
          <DialogDescription>{objectiveDescription(localState)}</DialogDescription>
        </DialogHeader>
        {medalThresholds ? <MedalThresholdScores thresholds={medalThresholds} /> : null}
        <DialogFooter>
          <Button type="button" size="lg" className="w-full" onClick={() => setOpen(false)}>
            Play
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
